import React, { useRef, useState } from 'react';
import { useAdVocalizeCore } from '../context/AdVocalizeCore';
import { Smartphone, Camera, Tv, Check, ChevronLeft, ChevronRight, RefreshCw, Maximize } from 'lucide-react';

const FORMATS = [
  { id: '9:16', label: 'STORY',  sub: 'REELS · TIKTOK',   icon: Smartphone, w: '18px', h: '32px' },
  { id: '1:1',  label: 'POST',   sub: 'IG · FACEBOOK',    icon: Camera,     w: '26px', h: '26px' },
  { id: '16:9', label: 'CINEMA', sub: 'YOUTUBE · WEB',    icon: Tv,         w: '42px', h: '24px' },
];

const FILTERS = [
  { id: 'none',     label: 'Original',   css: 'none' },
  { id: 'vivid',    label: 'Vivid',      css: 'saturate(1.5) contrast(1.1)' },
  { id: 'warm',     label: 'Warm',       css: 'sepia(0.45) brightness(1.05) saturate(1.2)' },
  { id: 'cool',     label: 'Cool',       css: 'saturate(0.8) brightness(1.05) hue-rotate(195deg)' },
  { id: 'noir',     label: 'Noir',       css: 'grayscale(1) contrast(1.1)' },
  { id: 'cinematic',label: 'Cinema',     css: 'contrast(1.12) saturate(0.78) brightness(0.94)' },
];

const Step1_CropStudio = () => {
    const { 
        image, 
        aspectRatio, setAspectRatio, 
        imgTransform, setImgTransform, 
        selectedFilter, setSelectedFilter, 
        fitMode, setFitMode,
        setStep,
        analyzePhase
    } = useAdVocalizeCore();

    const [isEditing, setIsEditing] = useState(false);
    const panStartRef = useRef(null);
    const activePointersRef = useRef(new Map());

    const onPointerDown = (e) => {
        if (!e.target.setPointerCapture) return;
        e.target.setPointerCapture(e.pointerId);
        activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (activePointersRef.current.size === 1) {
            panStartRef.current = { x: e.clientX - imgTransform.x, y: e.clientY - imgTransform.y };
        }
    };

    const onPointerMove = (e) => {
        if (!activePointersRef.current.has(e.pointerId)) return;
        activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (activePointersRef.current.size === 1 && panStartRef.current) {
            setImgTransform(prev => ({
                ...prev,
                x: e.clientX - panStartRef.current.x,
                y: e.clientY - panStartRef.current.y
            }));
        }
    };

    const onPointerUp = (e) => {
        activePointersRef.current.delete(e.pointerId);
        if (activePointersRef.current.size === 0) panStartRef.current = null;
    };

    const onWheel = (e) => {
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setImgTransform(prev => ({ ...prev, scale: Math.min(Math.max(prev.scale * delta, 0.5), 5) }));
    };

    const getCanvasRatio = () => {
        if (aspectRatio === '9:16') return 'aspect-[9/16] h-[550px]';
        if (aspectRatio === '16:9') return 'aspect-[16/9] w-full max-w-[700px]';
        return 'aspect-square w-[450px]';
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-3 animate-in slide-in-from-bottom-6 duration-700">
            <div className="bg-white/5 border border-white/10 rounded-[3.5rem] overflow-hidden backdrop-blur-3xl shadow-2xl grid lg:grid-cols-[1fr_360px] min-h-[700px]">
                
                {/* LEFT: Interactive Canvas */}
                <div className="relative bg-[#06091a] flex items-center justify-center p-12 border-r border-white/10 overflow-hidden">
                    {/* Background Grid/Pattern */}
                    <div className="absolute inset-10 rounded-3xl opacity-5 pointer-events-none" 
                         style={{ backgroundImage: `radial-gradient(circle, #6366f1 1px, transparent 1px)`, backgroundSize: '24px 24px' }} />

                    {/* The Canvas */}
                    <div 
                        className={`relative bg-black rounded-2xl overflow-hidden shadow-[0_40px_80px_rgba(0,0,0,0.8)] border border-indigo-500/30 ring-8 ring-indigo-500/5 transition-all duration-500 ${getCanvasRatio()} ${isEditing ? 'cursor-move' : 'cursor-default'}`}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerLeave={onPointerUp}
                        onWheel={onWheel}
                    >
                        <img 
                            src={image} 
                            alt="Crop Preview"
                            draggable={false}
                            className={`w-full h-full pointer-events-none transition-filter duration-300 ${fitMode === 'cover' ? 'object-cover' : 'object-contain'}`}
                            style={{
                                transform: `translate(${imgTransform.x}px, ${imgTransform.y}px) scale(${imgTransform.scale})`,
                                filter: FILTERS.find(f => f.id === selectedFilter)?.css || 'none'
                            }}
                        />

                        {/* Status Overlays */}
                        <div className="absolute top-6 left-6 px-4 py-2 bg-black/60 backdrop-blur-md border border-white/10 rounded-xl text-[10px] font-black font-mono tracking-widest text-indigo-400">
                            {aspectRatio.replace(':', ' / ')} · 1080P MASTER
                        </div>
                        {analyzePhase && (
                            <div className="absolute top-6 right-6 px-4 py-2 bg-black/70 backdrop-blur-md border border-indigo-500/40 rounded-xl text-[10px] font-black font-mono tracking-widest text-indigo-400 flex items-center gap-2 animate-pulse">
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                AI
                            </div>
                        )}

                        {/* Corner Handles (Visual only for now) */}
                        <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-indigo-400/50 rounded-tl-lg m-4" />
                        <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-indigo-400/50 rounded-tr-lg m-4" />
                        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-indigo-400/50 rounded-bl-lg m-4" />
                        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-indigo-400/50 rounded-br-lg m-4" />

                        {/* Interactive Toggle Button */}
                        <button 
                            onClick={() => setIsEditing(!isEditing)}
                            className="absolute bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 bg-black/70 hover:bg-black/90 backdrop-blur-xl border border-white/20 rounded-full flex items-center gap-3 transition-all group shadow-xl"
                        >
                            {isEditing ? (
                                <>
                                    <Check className="w-4 h-4 text-emerald-400" />
                                    <span className="text-[10px] font-black uppercase tracking-widest text-white">Apply Frame</span>
                                </>
                            ) : (
                                <>
                                    <Maximize className="w-4 h-4 text-indigo-400 group-hover:scale-110 transition-transform" />
                                    <span className="text-[10px] font-black uppercase tracking-widest text-white">Edit Frame</span>
                                </>
                            )}
                        </button>
                    </div>

                    {/* Canvas Metadata Footnote */}
                    <div className="absolute bottom-10 left-10 right-10 flex justify-between items-center opacity-40">
                         <span className="text-[10px] font-mono font-black tracking-[0.2em] text-slate-300">◇ SCROLL TO ZOOM · DRAG TO PAN</span>
                         <span className="text-[10px] font-mono font-black tracking-[0.2em] uppercase text-slate-300">72 DPI · CMYK OFF</span>
                    </div>
                </div>

                {/* RIGHT: Controls */}
                <div className="p-10 flex flex-col gap-10">
                    {/* Header */}
                    <div className="space-y-2">
                        <h3 className="text-2xl font-black tracking-tight text-white uppercase">Compose the <span className="text-indigo-400">frame</span></h3>
                        <p className="text-slate-300 text-sm font-bold leading-relaxed">Pick a surface, then style the visual.</p>
                    </div>

                    {/* Format Selector */}
                    <div className="space-y-4">
                        <label className="text-[11px] font-black uppercase tracking-[0.3em] text-white/90 px-1 border-l-2 border-indigo-500 pl-3">FORMAT</label>
                        <div className="flex flex-col gap-3">
                            {FORMATS.map(f => (
                                <button 
                                    key={f.id} 
                                    onClick={() => { setAspectRatio(f.id); setImgTransform({ x: 0, y: 0, scale: 1, rotate: 0 }); }}
                                    className={`group flex items-center justify-between p-4 rounded-2xl border-2 transition-all duration-500 ${aspectRatio === f.id ? 'bg-indigo-600/20 border-indigo-500 shadow-[0_0_30px_rgba(99,102,241,0.2)] scale-[1.02]' : 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/20'}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-xl border-2 flex items-center justify-center transition-all ${aspectRatio === f.id ? 'bg-indigo-600 border-indigo-400 text-white shadow-lg' : 'bg-black/40 border-white/10 text-slate-400 group-hover:text-white'}`}>
                                            <f.icon className="w-5 h-5" />
                                        </div>
                                        <div className="text-left">
                                            <p className={`font-black text-xs tracking-[0.1em] uppercase ${aspectRatio === f.id ? 'text-white' : 'text-slate-300'}`}>{f.label}</p>
                                            <p className={`text-[9px] font-bold uppercase tracking-widest mt-1 ${aspectRatio === f.id ? 'text-indigo-300' : 'text-slate-500'}`}>{f.sub}</p>
                                        </div>
                                    </div>
                                    <span className={`font-mono text-xs font-black ${aspectRatio === f.id ? 'text-indigo-300' : 'text-slate-600'}`}>{f.id}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Filter Selector */}
                    <div className="space-y-4">
                        <label className="text-[11px] font-black uppercase tracking-[0.3em] text-white/90 px-1 border-l-2 border-indigo-500 pl-3">FILTER</label>
                        <div className="grid grid-cols-3 gap-3">
                            {FILTERS.map(f => (
                                <button 
                                    key={f.id} 
                                    onClick={() => setSelectedFilter(f.id)}
                                    className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${selectedFilter === f.id ? 'border-orange-500 scale-105 shadow-2xl z-10' : 'border-transparent opacity-50 hover:opacity-100 hover:border-white/30'}`}
                                >
                                    <img 
                                        src={image} 
                                        className="w-full h-full object-cover pointer-events-none" 
                                        style={{ filter: f.css }}
                                        alt={f.label}
                                    />
                                    <div className={`absolute inset-x-0 bottom-0 py-1.5 text-[8px] font-black uppercase text-center backdrop-blur-md ${selectedFilter === f.id ? 'bg-orange-500 text-white' : 'bg-black/60 text-white/90'}`}>
                                        {f.label}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-auto space-y-4">
                        <button
                            onClick={() => setStep(2)}
                            className="w-full py-5 rounded-[1.5rem] font-black text-sm uppercase tracking-[0.2em] shadow-2xl transition-all active:scale-95 flex items-center justify-center gap-3 group relative overflow-hidden bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-600/40"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
                            {analyzePhase && <RefreshCw className="w-4 h-4 animate-spin opacity-60" />}
                            Next: Ad Studio
                            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button 
                            onClick={() => setStep(0)}
                            className="w-full py-3 bg-white/5 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-white hover:bg-white/10 transition-all"
                        >
                            ← Change visual
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Step1_CropStudio;
